/**
 * Maximum length of the hyperlink of a content area (dbo.ContentArea.OnClickUri nvarchar(256),
 * [MaxLength(256)] on Create/UpdateContentAreaDto.OnClickUri in the API). The single place to
 * change when the backend limit is raised.
 */
export const CONTENT_AREA_ON_CLICK_URI_MAX_LENGTH = 256;

/** True if the hyperlink fits into the backend column. Empty values are not this rule's concern. */
export function isOnClickUriWithinLimit(
  uri: string | null | undefined,
  maxLength: number = CONTENT_AREA_ON_CLICK_URI_MAX_LENGTH,
): boolean {
  return (uri?.length ?? 0) <= maxLength;
}

/**
 * The hyperlink to save: `null` while the "clickable" option is off, otherwise the entered value.
 */
export function resolveOnClickUri(clickable: boolean, uri: string): string | null {
  return clickable ? uri : null;
}

/** Validation of a Vuetify form (`<v-form>`), as far as the save needs it. */
interface ValidatableForm {
  validate: () => Promise<{ valid: boolean }>;
}

/**
 * A save may only continue when the hyperlink is not enabled or its form is valid. The form is
 * validated first, so the field shows its error.
 */
export async function validateOnClickUri(clickable: boolean, form: ValidatableForm | undefined): Promise<boolean> {
  if (!clickable)
    return true;
  const result = await form?.validate();
  return result?.valid ?? false;
}

/** Vuetify rule: the hyperlink has to fit into the backend column, else the message is returned. */
export function createOnClickUriMaxLengthRule(
  translate: (key: string, params: { max: number }) => string,
): (uri: string | null | undefined) => true | string {
  return uri => isOnClickUriWithinLimit(uri)
    || translate(
      "pages.marketing.contentManagement.contentManagementDialog.rules.urlTooLong",
      { max: CONTENT_AREA_ON_CLICK_URI_MAX_LENGTH },
    );
}
